import React from 'react';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import { X, CheckCircle, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import './Toast.css';

const ToastContainer = () => {
  const { toasts, removeToast } = useToast();
  const { theme } = useTheme();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onRemove={() => removeToast(toast.id)} 
          theme={theme} 
        />
      ))}
    </div>
  );
};

const ToastItem = ({ toast, onRemove, theme }) => {
  const { message, type, duration } = toast;

  const config = {
    success: {
      icon: CheckCircle,
      bg: 'bg-white',
      border: 'border-green-100',
      text: 'text-green-800',
      progress: 'bg-green-500',
      iconColor: 'text-green-500'
    },
    error: {
      icon: AlertCircle,
      bg: 'bg-white',
      border: 'border-red-100',
      text: 'text-red-800',
      progress: 'bg-red-500',
      iconColor: 'text-red-500'
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-white',
      border: 'border-yellow-100',
      text: 'text-yellow-800',
      progress: 'bg-yellow-500',
      iconColor: 'text-yellow-500'
    },
    info: {
      icon: Info,
      bg: 'bg-white',
      border: 'border-blue-100',
      text: 'text-blue-800',
      progress: 'bg-blue-500',
      iconColor: 'text-blue-500'
    }
  }[type] || {
    icon: Info,
    bg: 'bg-white',
    border: 'border-gray-100',
    text: 'text-gray-800',
    progress: 'bg-gray-500',
    iconColor: 'text-gray-500'
  };

  const Icon = config.icon;

  return (
    <div 
      className={`pointer-events-auto flex items-center gap-3 px-4 py-3 min-w-[300px] max-w-[450px] rounded-xl shadow-lg border ${config.bg} ${config.border} animate-toast-slide-in relative overflow-hidden`}
    >
      <div className={`p-1.5 rounded-lg ${config.iconColor} bg-opacity-10`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className={`flex-1 text-sm font-medium ${config.text}`}>
        {message}
      </div>
      <button 
        onClick={onRemove}
        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>
      
      {duration !== Infinity && (
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gray-100">
          <div 
            className={`h-full ${config.progress} animate-den-toast-progress`} 
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      )}
    </div>
  );
};

export default ToastContainer;
